import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { leaveAdminAPI } from '../services/api';

interface ImportError {
  row: number;
  codice_fiscale: string;
  error: string;
}

interface ImportResult {
  total_rows: number;
  success_count: number;
  error_count: number;
  skipped_count: number;
  errors: ImportError[];
}

const LeaveBalanceImport: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setFile(event.target.files[0]);
      setResult(null); // Reset risultato precedente
    }
  };

  const handleImport = async () => {
    if (!file) {
      return;
    }

    setImporting(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      // Non inviamo più l'anno: i dati vengono riportati così come sono dall'Excel senza distinzione per anno

      const response = await leaveAdminAPI.importBalances(formData);
      setResult(response);
    } catch (error: any) {
      console.error('Errore durante l\'import:', error);
      alert(`Errore durante l'import: ${error.message || 'Errore sconosciuto'}`);
    } finally {
      setImporting(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Card>
        <CardContent>
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3 }}>
            Import Saldi Ferie da Excel
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Carica un file Excel con la struttura seguente:
            <br />
            <strong>Colonne richieste:</strong> Operatore, Ente, ORE Sett., CF, ferie, permessi, ex festività
            <br />
            Il file deve contenere i saldi ferie/permessi per ciascun dipendente.
            <br />
            <strong>Nota:</strong> I dati vengono riportati così come sono esposti nel file Excel, senza calcoli o distinzioni per anno.
          </Typography>

          <Box sx={{ mt: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Button variant="contained" component="label" startIcon={<CloudUploadIcon />}>
                Seleziona File Excel
                <input
                  type="file"
                  hidden
                  accept=".xlsx,.xls"
                  onChange={handleFileChange}
                />
              </Button>

              {file && (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  File selezionato: <strong>{file.name}</strong>
                </Typography>
              )}
            </Box>

          </Box>

          <Button
            variant="contained"
            color="primary"
            onClick={handleImport}
            disabled={!file || importing}
            sx={{ mt: 3 }}
            fullWidth
          >
            {importing ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Importazione in corso...
              </>
            ) : (
              'Importa Saldi'
            )}
          </Button>

          {result && (
            <Box sx={{ mt: 4 }}>
              <Alert
                severity={result.error_count > 0 ? 'warning' : 'success'}
                sx={{ mb: 3 }}
              >
                <strong>Import completato!</strong>
                <br />
                Totale righe: {result.total_rows} | Successi: {result.success_count} | Errori: {result.error_count}
              </Alert>

              {result.errors && result.errors.length > 0 && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2, color: 'error.main' }}>
                    Errori durante l'import ({result.errors.length})
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ffebee' }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>Riga</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Codice Fiscale</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Errore</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {result.errors.map((error, index) => (
                          <TableRow key={index} hover>
                            <TableCell>
                              <Chip label={`Riga ${error.row}`} size="small" color="error" />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold">
                                {error.codice_fiscale || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="error">
                                {error.error}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {result.success_count > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Alert severity="info">
                    {result.success_count} saldi sono stati importati correttamente.
                    <br />
                    Puoi visualizzare i saldi aggiornati nella sezione "Saldi Ferie e Permessi".
                  </Alert>
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default LeaveBalanceImport;
